"use client";

import React, { useState, useEffect } from 'react';
import { intervalToDuration, formatDuration, isPast } from 'date-fns';
import type { Duration } from 'date-fns';
import { es } from 'date-fns/locale';

interface CountdownTimerProps {
  endTime: string; // ISO string date
  onAuctionEnd?: () => void; // Optional callback when countdown finishes
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ endTime, onAuctionEnd }) => {
  const [remainingDuration, setRemainingDuration] = useState<Duration | null>(null);
  const [isAuctionFinished, setIsAuctionFinished] = useState(false);

  useEffect(() => {
    // local flag to ensure onAuctionEnd is called only once
    let endedCalled = false;

    const calculateRemainingTime = () => {
      const now = new Date();
      const targetDate = new Date(endTime);
      if (isPast(targetDate)) {
        setRemainingDuration(null);
        setIsAuctionFinished(true);
        if (!endedCalled) {
          endedCalled = true;
          onAuctionEnd?.();
        }
        return;
      }

      const duration = intervalToDuration({ start: now, end: targetDate });
      setRemainingDuration(duration);
      setIsAuctionFinished(false);
    };

    // Calculate immediately on mount
    calculateRemainingTime();

    // Update every second
    const timer = setInterval(() => {
      calculateRemainingTime();
      // If auction finished, clear interval to stop further updates
      if (endedCalled) clearInterval(timer);
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(timer);
  }, [endTime, onAuctionEnd]);

  if (isAuctionFinished) {
    return <span>Subasta Finalizada</span>;
  }

  if (!remainingDuration) {
    return <span>Cargando...</span>;
  }

  const formattedDuration = formatDuration(remainingDuration, {
    locale: es,
    format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'],
    delimiter: ', ',
    zero: false, // Don't show zero units unless it's the only unit (e.g., "0 segundos")
    // Custom format to be more concise for smaller units
    // Example: "1 día, 2 horas, 30 minutos"
    // If only seconds left: "30 segundos"
    // If only minutes and seconds: "5 minutos, 10 segundos"
  });

  // Fallback for very short durations to ensure seconds are always shown if present
  const parts = [];
  if (remainingDuration.days && remainingDuration.days > 0) parts.push(`${remainingDuration.days}d`);
  if (remainingDuration.hours && remainingDuration.hours > 0) parts.push(`${remainingDuration.hours}h`);
  if (remainingDuration.minutes && remainingDuration.minutes > 0) parts.push(`${remainingDuration.minutes}m`);
  if (remainingDuration.seconds && remainingDuration.seconds > 0) parts.push(`${remainingDuration.seconds}s`);

  // If no parts, but not finished, it means it's very close to ending, show 0s
  if (parts.length === 0 && !isAuctionFinished) {
    return <span>0s</span>;
  }

  return <span>{parts.join(' ')}</span>;
};

export default CountdownTimer;